// frontend/src/contexts/NotificationContext.jsx

import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo
} from 'react';
import { useSocket } from './SocketContext';
import { useAuth } from './AuthContext';
import logger from '../utils/logger';

const defaultNotificationContextValue = {
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  fetchNotifications: () => console.warn('NotificationProvider not found'),
  markAsRead: () => console.warn('NotificationProvider not found'),
  markAllAsRead: () => console.warn('NotificationProvider not found'),
  hasMoreNotifications: false,
  loadMoreNotifications: () => console.warn('NotificationProvider not found'),
  currentPage: 1,
  totalPages: 0,
  initialLoadComplete: false,
};

// FIX: Remove one of the 'export' keywords
export const NotificationContext = createContext(defaultNotificationContextValue);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const socket = useSocket();

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [hasMoreNotifications, setHasMoreNotifications] = useState(false);
  const [activeFilter, setActiveFilter] = useState({});
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  const NOTIFICATIONS_PER_PAGE = 15;

  const loadNotificationsData = useCallback(async (pageToFetch = 1, merge = false, filterToUse = {}) => {
    if (!isAuthenticated) {
      logger.debug('[NotificationContext] loadNotificationsData: Not authenticated, clearing state.');
      setNotifications([]); setUnreadCount(0); setCurrentPage(1);
      setTotalPages(0); setHasMoreNotifications(false); setInitialLoadComplete(false);
      return;
    }
    logger.debug(`[NotificationContext] loadNotificationsData: Fetching page ${pageToFetch}, merge: ${merge}, filter:`, filterToUse);
    setIsLoading(true);

    try {
      const apiServiceModule = (await import('../services/api')).default; 
      
      if (apiServiceModule.notifications && typeof apiServiceModule.notifications.getNotifications === 'function') {
        const apiParams = { page: pageToFetch, limit: NOTIFICATIONS_PER_PAGE, ...filterToUse };
        const response = await apiServiceModule.notifications.getNotifications(apiParams);

        const fetchedNotifications = response.notifications || response.data?.notifications || [];
        const paginationInfo = response.pagination || {};

        setNotifications(prev => merge ? [...prev, ...fetchedNotifications] : fetchedNotifications);
        setUnreadCount(paginationInfo.totalUnread || 0);
        setCurrentPage(paginationInfo.currentPage || 1);
        setTotalPages(paginationInfo.totalPages || 0);
        setHasMoreNotifications((paginationInfo.currentPage || 1) < (paginationInfo.totalPages || 0));
        if (pageToFetch === 1 && !merge) setInitialLoadComplete(true);
      } else {
        logger.error("NotificationContext: apiService.notifications.getNotifications is not available. Check import/export or module loading order.");
        if (pageToFetch === 1 && !merge) {
          setNotifications([]); setUnreadCount(0); setCurrentPage(1);
          setTotalPages(0); setHasMoreNotifications(false); setInitialLoadComplete(false);
        }
      }
    } catch (error) {
      logger.error("NotificationContext: Failed to fetch notifications", error.response?.data || error);
      if (pageToFetch === 1 && !merge) {
        setNotifications([]); setUnreadCount(0); setCurrentPage(1);
        setTotalPages(0); setHasMoreNotifications(false); setInitialLoadComplete(false);
      }
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, NOTIFICATIONS_PER_PAGE]);

  useEffect(() => {
    if (isAuthenticated) {
      setInitialLoadComplete(false);
      loadNotificationsData(1, false, activeFilter);
    } else {
      setNotifications([]);
      setUnreadCount(0);
      setCurrentPage(1);
      setTotalPages(0);
      setHasMoreNotifications(false);
      if (Object.keys(activeFilter).length > 0) {
        logger.debug('[NotificationContext] useEffect: Resetting activeFilter because it was not empty and user is not authenticated.');
        setActiveFilter({});
      }
      setInitialLoadComplete(false);
    }
  }, [isAuthenticated, activeFilter, loadNotificationsData]);

  const fetchNotificationsWithFilter = useCallback((newFilterParams) => {
    setActiveFilter(newFilterParams || {});
  }, []);

  const loadMoreNotifications = useCallback(async () => {
    if (!isLoading && hasMoreNotifications && isAuthenticated) {
      loadNotificationsData(currentPage + 1, true, activeFilter);
    }
  }, [isLoading, hasMoreNotifications, isAuthenticated, currentPage, activeFilter, loadNotificationsData]);

  const markAsRead = useCallback(async (notificationId) => {
    const notificationToMark = notifications.find(n => n.id === notificationId && !n.read_at);
    if (!notificationToMark) return;

    setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, read_at: new Date().toISOString() } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));

    try {
      const apiServiceModule = (await import('../services/api')).default;
      if (apiServiceModule.notifications && typeof apiServiceModule.notifications.markNotificationAsRead === 'function') {
        await apiServiceModule.notifications.markNotificationAsRead(notificationId);
      } else {
        logger.error("NotificationContext: apiService.notifications.markNotificationAsRead is not available.");
        throw new Error("API method not found.");
      }
    } catch (error) {
      logger.error(`NotificationContext: Failed to mark notification ${notificationId} as read. Reverting.`, error.response?.data || error);
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, read_at: notificationToMark.read_at } : n));
      setUnreadCount(prev => prev + 1);
    }
  }, [notifications, unreadCount]);

  const markAllAsRead = useCallback(async () => {
    if (unreadCount === 0) return;

    const now = new Date().toISOString();
    setNotifications(prev => prev.map(n => n.read_at ? n : { ...n, read_at: now }));
    setUnreadCount(0);

    try {
      const apiServiceModule = (await import('../services/api')).default;
      if (apiServiceModule.notifications && typeof apiServiceModule.notifications.markAllNotificationsAsRead === 'function') {
        await apiServiceModule.notifications.markAllNotificationsAsRead();
      } else {
        logger.error("NotificationContext: apiService.notifications.markAllNotificationsAsRead is not available.");
        throw new Error("API method not found.");
      }
    } catch (error) {
      logger.error("NotificationContext: Failed to mark all notifications as read. Reverting.", error.response?.data || error);
      loadNotificationsData(1, false, activeFilter);
    }
  }, [unreadCount, notifications, activeFilter, loadNotificationsData]);

  useEffect(() => {
    if (socket && isAuthenticated) {
      const handleNewNotification = (newNotification) => {
        logger.debug("NotificationContext: WebSocket 'new_notification'", newNotification);
        if (!newNotification || !newNotification.id) return;

        let matchesFilter = true;
        // The COMPONENT_NAME variable is not defined here. Commenting out or defining it.
        // if (activeFilter.type && newNotification.type !== activeFilter.type) matchesFilter = false;
        // if (activeFilter.unreadOnly && newNotification.read_at) matchesFilter = false;

        if (matchesFilter) {
          setNotifications(prev => {
            if (prev.some(n => n.id === newNotification.id)) {
              // logger.debug(`[${COMPONENT_NAME}] Duplicate notification ${newNotification.id} received, ignoring.`);
              return prev;
            }
            return [newNotification, ...prev].slice(0, NOTIFICATIONS_PER_PAGE * 2);
          });
        }
        if (!newNotification.read_at) {
          setUnreadCount(prev => prev + 1);
        }
      };

      const handleNotificationReadUpdate = ({ notificationIds, allRead, unreadCount: newServerUnreadCount }) => {
        logger.debug("NotificationContext: WebSocket 'notification_read_update'", { notificationIds, allRead, newServerUnreadCount });
        const now = new Date().toISOString();
        if (allRead) {
          setNotifications(prev => prev.map(n => ({ ...n, read_at: n.read_at || now })));
          setUnreadCount(0);
        } else if (notificationIds && Array.isArray(notificationIds)) {
          setNotifications(prev => prev.map(n => notificationIds.includes(n.id) ? { ...n, read_at: now } : n));
          if (typeof newServerUnreadCount === 'number') {
            setUnreadCount(newServerUnreadCount);
          } else {
            const justReadCount = notificationIds.filter(id => {
              const currentNotif = notifications.find(n => n.id === id);
              return currentNotif && !currentNotif.read_at;
            }).length;
            setUnreadCount(prev => Math.max(0, prev - justReadCount));
          }
        } else if (typeof newServerUnreadCount === 'number') {
             setUnreadCount(newServerUnreadCount);
        }
      };

      socket.on('new_notification', handleNewNotification);
      socket.on('notification_read_update', handleNotificationReadUpdate);
      return () => {
        socket.off('new_notification', handleNewNotification);
        socket.off('notification_read_update', handleNotificationReadUpdate);
      };
    }
  }, [socket, isAuthenticated, activeFilter, notifications, NOTIFICATIONS_PER_PAGE]);

  const contextValue = useMemo(() => ({
    notifications,
    unreadCount,
    isLoading,
    fetchNotifications: fetchNotificationsWithFilter,
    markAsRead,
    markAllAsRead,
    hasMoreNotifications,
    loadMoreNotifications,
    currentPage,
    totalPages,
    initialLoadComplete
  }), [
    notifications, unreadCount, isLoading,
    fetchNotificationsWithFilter, markAsRead, markAllAsRead,
    hasMoreNotifications, loadMoreNotifications, currentPage, totalPages,
    initialLoadComplete
  ]);

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};